import lodash from 'lodash'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  GeckoEditableComponent,
  SceneConfig,
} from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoConeCourse,
  GeckoConeCourseJSON,
} from 'types/assetModel/features/legacyCone/geckoConeCourse'
import {
  GeckoConicalPlate,
  GeckoConicalPlateJSON,
} from 'types/assetModel/features/legacyCone/geckoConicalPlate'
import {
  GeckoConeCircumferentialWeld,
  GeckoConeCircumferentialWeldJSON,
} from 'types/assetModel/features/welds/geckoConeCircumferentialWeld'
import {
  GeckoConeRadialWeld,
  GeckoConeRadialWeldJSON,
} from 'types/assetModel/features/welds/geckoConeRadialWeld'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  getNumberFieldByDataKey,
  getStringFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseDate, parseNumber } from 'utils/parsingHelpers'
import {
  optionalDateStringWithRegex,
  optionalNumberSchema,
  requiredConeAngle,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'
import { v4 as uuid4 } from 'uuid'

export type ConeAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.Cone
  outer_radius: number
  inner_radius: number
  angle: number
  installation_date: string | undefined
  scene_config: SceneConfig
  features: Record<
    string,
    | GeckoConicalPlateJSON
    | GeckoConeCourseJSON
    | GeckoConeCircumferentialWeldJSON
    | GeckoConeRadialWeldJSON
  >
  component_bounds: {
    y_start: number
    y_end: number
  }
  compass_orientation: number | undefined
}

export const ConeAvailableFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.ConeCourse,
  GeckoFeatureModelType.ConePlate,
  GeckoFeatureModelType.ConeCircumferentialWeld,
  GeckoFeatureModelType.ConeRadialWeld,
]

export const NewConeAssetModelJSON = {
  name: 'New Cone',
  type: GeckoComponentModelType.Cone,
  outer_radius: 0,
  inner_radius: 0,
  angle: 0,
  installation_date: undefined,
  scene_config: {},
  component_bounds: {
    y_start: 0,
    y_end: 0,
  },
  features: {},
  compass_orientation: undefined,
} as ConeAssetModelJSON

export class GeckoCone extends GeckoEditableComponent {
  type: GeckoComponentModelType.Cone
  outerRadius: GeckoMeasurement
  innerRadius: GeckoMeasurement
  angle: GeckoMeasurement
  installationDate: Date | undefined
  componentBounds: GeckoComponentRect
  sceneConfig: SceneConfig
  courses: Map<string, GeckoConeCourse>
  plates: Map<string, GeckoConicalPlate>
  coneCircumferentialWelds: Map<string, GeckoConeCircumferentialWeld>
  coneRadialWelds: Map<string, GeckoConeRadialWeld>
  compassOrientation: number | undefined

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    outerRadius: GeckoMeasurement,
    innerRadius: GeckoMeasurement,
    angle: GeckoMeasurement,
    installationDate: Date | undefined,
    componentBounds: GeckoComponentRect,
    sceneConfig: SceneConfig,
    courses: Map<string, GeckoConeCourse>,
    plates: Map<string, GeckoConicalPlate>,
    coneCircumferentialWelds: Map<string, GeckoConeCircumferentialWeld>,
    coneRadialWelds: Map<string, GeckoConeRadialWeld>,
    compassOrientation: number | undefined
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.Cone
    this.outerRadius = outerRadius
    this.innerRadius = innerRadius
    this.angle = angle
    this.installationDate = installationDate
    this.componentBounds = componentBounds
    this.sceneConfig = sceneConfig
    this.courses = courses
    this.plates = plates
    this.coneCircumferentialWelds = coneCircumferentialWelds
    this.coneRadialWelds = coneRadialWelds
    this.compassOrientation = compassOrientation
  }

  public static CreateFromJSON(id: string, json: ConeAssetModelJSON) {
    const name = json.name as string
    const outerRadius = GeckoMeasurement.CreateFromValue(
      json.outer_radius as number,
      GeckoMeasurementUnit.Meters
    )
    const innerRadius = GeckoMeasurement.CreateFromValue(
      json.inner_radius as number,
      GeckoMeasurementUnit.Meters
    )

    const angle = GeckoMeasurement.CreateFromValue(
      json.angle as number,
      GeckoMeasurementUnit.Degrees
    )
    const installationDate = parseDate(json.installation_date)

    const featureData = json.features as Record<
      string,
      | GeckoConicalPlateJSON
      | GeckoConeCircumferentialWeldJSON
      | GeckoConeRadialWeldJSON
      | GeckoConeCourseJSON
    >

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      0,
      componentBoundsData.y_start,
      360,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Degrees,
      GeckoMeasurementUnit.Meters
    )

    const sceneConfig = json.scene_config as SceneConfig

    const childCourseJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ConeCourse
    })
    const courses = GeckoConeCourse.CreateMapFromJSON(
      childCourseJSON as Record<string, GeckoConeCourseJSON>,
      componentBounds,
      GeckoMeasurementUnit.Meters
    )
    const childPlateJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ConePlate
    })
    const plates = GeckoConicalPlate.CreateMapFromJSON(
      id,
      childPlateJSON as Record<string, GeckoConicalPlateJSON>
    )
    const childConeCircumferentialWeldJSON = lodash.pickBy(
      featureData,
      (child) => {
        return child.type === GeckoFeatureModelType.ConeCircumferentialWeld
      }
    )
    const coneCircumferentialWelds =
      GeckoConeCircumferentialWeld.CreateMapFromJSON(
        id,
        childConeCircumferentialWeldJSON as Record<
          string,
          GeckoConeCircumferentialWeldJSON
        >
      )

    const childConeRadialWeldJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ConeRadialWeld
    })
    const coneRadialWelds = GeckoConeRadialWeld.CreateMapFromJSON(
      id,
      childConeRadialWeldJSON as Record<string, GeckoConeRadialWeldJSON>
    )
    const compassOrientation = parseNumber(json.compass_orientation)
    return new GeckoCone(
      id,
      json,
      name,
      outerRadius,
      innerRadius,
      angle,
      installationDate,
      componentBounds,
      sceneConfig,
      courses,
      plates,
      coneCircumferentialWelds,
      coneRadialWelds,
      compassOrientation
    )
  }

  public get coursesArray() {
    return Array.from(this.courses.values())
  }

  public get platesArray() {
    return Array.from(this.plates.values())
  }

  public get coneRadialWeldsArray() {
    return Array.from(this.coneRadialWelds.values())
  }

  public get coneCircumferentialWeldsArray() {
    return Array.from(this.coneCircumferentialWelds.values())
  }

  public get coneHeight() {
    const angleRadians = this.angle.toNumber(GeckoMeasurementUnit.Radians)
    const triangleHeight = this.outerRadius.num * Math.tan(angleRadians)
    const heightDifference =
      (this.innerRadius.num * triangleHeight) / this.outerRadius.num
    const coneHeight = triangleHeight - heightDifference
    return GeckoMeasurement.CreateFromValue(
      coneHeight,
      GeckoMeasurementUnit.Meters
    )
  }

  public get slopeLength() {
    const angleRadians = this.angle.toNumber(GeckoMeasurementUnit.Radians)
    const hypotenuse = this.outerRadius.num / Math.cos(angleRadians)
    const hypotenuseDifference =
      (this.innerRadius.num * hypotenuse) / this.outerRadius.num
    const slopeLength = hypotenuse - hypotenuseDifference
    return GeckoMeasurement.CreateFromValue(
      slopeLength,
      GeckoMeasurementUnit.Meters
    )
  }

  public asJSON(): ConeAssetModelJSON {
    return generateCone(
      this.name,
      this.outerRadius,
      this.innerRadius,
      this.angle,
      this.installationDate,
      this.compassOrientation,
      this.json as ConeAssetModelJSON
    )
  }
}

const generateCone = (
  name: string,
  outerRadius: GeckoMeasurement,
  innerRadius: GeckoMeasurement,
  angle: GeckoMeasurement,
  installationDate: Date | undefined,
  compassOrientation: number | undefined,
  otherData: ConeAssetModelJSON = NewConeAssetModelJSON
): ConeAssetModelJSON => {
  const outerRadiusMeters = outerRadius
    .convertTo(GeckoMeasurementUnit.Meters)
    .round(assetModelPrecision)
  const innerRadiusMeters = innerRadius
    .convertTo(GeckoMeasurementUnit.Meters)
    .round(assetModelPrecision)
  const coneInstallationDate = installationDate
    ? installationDate.toISOString().split('T')[0]
    : undefined

  // ensure all cones have at least one course (used for nominal inputs)
  const hasCourse = lodash.some(
    Object.values(otherData.features),
    (feature) => feature.type === GeckoFeatureModelType.ConeCourse
  )
  const featureSetToUse: ConeAssetModelJSON['features'] = {
    ...otherData.features,
    ...(hasCourse
      ? {}
      : {
          [`course-${uuid4().slice(0, 6)}`]: {
            type: GeckoFeatureModelType.ConeCourse,
            name: 'Course 1',
            feature_geometry: {
              y_end: outerRadiusMeters.num,
            },
            tmin: undefined,
            nominal: {
              wall_thickness: undefined,
              coating_thickness: undefined,
            },
          },
        }),
  }

  return {
    name: name,
    type: GeckoComponentModelType.Cone,
    outer_radius: outerRadiusMeters.num,
    inner_radius: innerRadiusMeters.num,
    angle: angle.num,
    installation_date: coneInstallationDate,
    scene_config: otherData.scene_config,
    features: featureSetToUse,
    component_bounds: {
      y_start: innerRadius.num,
      y_end: outerRadius.num,
    },
    compass_orientation: compassOrientation,
  }
}

export const ConeAssetModelFormFields = (JSON: ConeAssetModelJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Legacy 3D Cone Name',
      value: JSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    // Diameter is the industry standard measurement for tanks, so convert to radius and back
    {
      dataKey: 'outer_radius',
      label: 'Outer Diameter',
      value: JSON.outer_radius * 2,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    // Diameter is the industry standard measurement for tanks, so convert to radius and back
    {
      dataKey: 'inner_radius',
      label: 'Inner Diameter',
      value: JSON.inner_radius * 2,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'angle',
      label: 'Angle',
      value: JSON.angle,
      unit_magnitude: UnitMagnitude.Degrees,
      validationRule: requiredConeAngle,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'installation_date',
      label: 'Installation Date',
      value: JSON.installation_date,
      unit_magnitude: null,
      validationRule: optionalDateStringWithRegex,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Date,
    },

    {
      dataKey: 'compass_orientation',
      label: 'Compass Orientation',
      value: JSON.compass_orientation,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const ConeJSONFromFormFields = (
  component: ConeAssetModelJSON,
  formFields: FormField[]
) => {
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  return generateCone(
    getStringFieldByDataKey(formFields, 'name') ?? '',
    GeckoMeasurement.CreateFromValue(
      (getNumberFieldByDataKey(formFields, 'outer_radius') ?? 0) / 2, //radius is diameter
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      (getNumberFieldByDataKey(formFields, 'inner_radius') ?? 0) / 2, //radius is diameter
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getNumberFieldByDataKey(formFields, 'angle') ?? 0,
      GeckoMeasurementUnit.Degrees
    ),
    installationDate?.value
      ? new Date(installationDate?.value as string)
      : undefined,
    getNumberFieldByDataKey(formFields, 'compass_orientation') ?? 0,
    component
  )
}
